import {
  AuditColumnSelection,
  AuditDateRange,
  AuditLocation,
  AuditRow,
  turbineSelectionKey
} from './types';
import { NestedAccordionLocation } from '@/components/DataTable';
import { Turbine } from '@/containers/GlobalViewContainer/types';
import {
  AuditReportRequest,
  AuditReportResponseLocation
} from '@/hooks/services/Audit';
import {
  getMonthToDateRange,
  getShortUSFormatDate,
  getTimezoneAbbreviation
} from '@/solaris-lens-common-lib/src/utils/dates';

export const getDefaultAuditDateRange = (): AuditDateRange =>
  getMonthToDateRange();

export const isSameAuditRequest = (
  current: AuditReportRequest | null | undefined,
  next: AuditReportRequest
): boolean =>
  Boolean(current) && JSON.stringify(current) === JSON.stringify(next);

export const AUDIT_FUEL_FILTER = 'fuelData';
export const AUDIT_ENGINE_HOURS_FILTER = 'engineFiredHoursAnalog';
export const AUDIT_MONTHLY_STARTS_FILTER = 'monthlyStartsAnalog';
export const AUDIT_MONTHLY_STOPS_FILTER = 'monthlyStopsAnalog';

export const buildAuditFilters = (
  selection: AuditColumnSelection
): string[] => {
  const filters: string[] = [];

  if (selection.showFuel) {
    filters.push(AUDIT_FUEL_FILTER);
  }

  if (selection.showEngineHours) {
    filters.push(AUDIT_ENGINE_HOURS_FILTER);
  }

  if (selection.showMonthlyStarts) {
    filters.push(AUDIT_MONTHLY_STARTS_FILTER);
  }

  if (selection.showMonthlyStops) {
    filters.push(AUDIT_MONTHLY_STOPS_FILTER);
  }

  return filters;
};

export const buildAuditLocations = (turbines: Turbine[]): AuditLocation[] => {
  const locationsMap = new Map<string, AuditLocation>();

  turbines.forEach(turbine => {
    if (!turbine.locationId) return;

    const existing = locationsMap.get(turbine.locationId);
    const turbineEntry = {
      id: turbine.id,
      name: turbine.name,
      subType: turbine.subType
    };

    if (existing) {
      existing.turbines.push(turbineEntry);
      if (!existing.timezone && turbine.timezone) {
        existing.timezone = turbine.timezone;
      }
      return;
    }

    locationsMap.set(turbine.locationId, {
      id: turbine.locationId,
      title: turbine.locationName || turbine.locationId,
      timezone: turbine.timezone || '',
      turbines: [turbineEntry]
    });
  });

  return Array.from(locationsMap.values()).sort((a, b) =>
    a.title.localeCompare(b.title)
  );
};

export const buildReportRequest = (
  range: AuditDateRange,
  locations: AuditLocation[],
  selectedKeys: Record<string, boolean>,
  filters: string[]
): AuditReportRequest | null => {
  const requestLocations = locations
    .map(location => ({
      id: location.id,
      name: location.title,
      timezone: location.timezone,
      turbines: location.turbines
        .filter(
          turbine => selectedKeys[turbineSelectionKey(location.id, turbine.id)]
        )
        .map(turbine => ({
          id: turbine.id,
          name: turbine.name,
          subType: turbine.subType
        }))
    }))
    .filter(location => location.turbines.length > 0 && location.timezone);

  if (requestLocations.length === 0 || filters.length === 0) {
    return null;
  }

  return {
    startDate: range.start,
    endDate: range.end,
    locations: requestLocations,
    filters
  };
};

export const mapReportToGroups = (
  reportLocations: AuditReportResponseLocation[]
): NestedAccordionLocation<AuditRow>[] =>
  reportLocations.map(location => {
    const timezoneAbbr = getTimezoneAbbreviation({
      timezone: location.timezone
    });

    return {
      id: location.id,
      title: location.name,
      subtitle: location.city
        ? `${location.city}${location.state ? `, ${location.state}` : ''}`
        : undefined,
      turbines: location.turbines.map(turbine => ({
        id: turbine.id,
        title: turbine.id,
        subtitle: turbine.subType,
        rows: turbine.dataRecords.map((record, index) => ({
          id: `${turbine.id}-${index}`,
          turbine: turbine.id,
          date: getShortUSFormatDate(record.date),
          timezone: timezoneAbbr,
          fuelData: record.fuelData ?? null,
          engineFiredHours: record.engineFiredHoursAnalog ?? null,
          monthlyStarts: record.monthlyStarts ?? null,
          monthlyStops: record.monthlyStops ?? null
        }))
      }))
    };
  });

export const downloadBlob = (filename: string, blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
